import { Entity } from '@/domain/document-processing/types/entity-types';
import { httpRequest } from '@/lib/http-client';
import type { HumanIdentityReview } from '@/types/identity-review-types';
import { LoggingService } from './LoggingService';
import { captureEvent, captureException } from '@/config/posthog';
import { captureException as sentryCaptureException } from '@/config/sentry';
import {
  UnresolvedVisualIdentity,
} from '@/types/document-types';

const logger = new LoggingService('EntityExportService');

interface ExportResult {
  success: boolean;
  contentType: string;
  filename: string;
  data: string;
  size?: number; // Optional size field for PDF
}

export class EntityExportService {
  private static baseUrl = '/api/review/entity';

  /**
   * Export entities to JSON format
   * @param entities Array of entities to export
   * @returns Base64 encoded JSON data with metadata
   */
  static async exportToJson(
    entities: Entity[],
    unresolvedVisualIdentities: UnresolvedVisualIdentity[] = [],
    humanIdentityReviews: HumanIdentityReview[] = []
  ): Promise<ExportResult> {
    logger.info('Exporting entities to JSON', {
      entityCount: entities.length,
    });
    const startTime = Date.now();

    captureEvent('client_json_export_started', {
      entityCount: entities.length,
    });

    try {
      const result = await httpRequest<ExportResult>(`${this.baseUrl}/json`, {
        method: 'POST',
        data: {
          entities,
          unresolvedVisualIdentities,
          humanIdentityReviews
        }
      });

      logger.info('Entities exported to JSON successfully', {
        filename: result.filename
      });

      captureEvent('client_json_export_completed', {
        entityCount: entities.length,
        filename: result.filename,
        processingTimeMs: Date.now() - startTime,
      });

      return result;
    } catch (error) {
      logger.error('Error exporting entities to JSON:', error);
      const err = error instanceof Error ? error : new Error('JSON export failed');
      captureException(err, {
        entityCount: entities.length,
        processingTimeMs: Date.now() - startTime,
      });
      sentryCaptureException(err, { exportType: 'json', entityCount: entities.length });
      throw err;
    }
  }

  /**
   * Export entities to PDF format
   * @param htmlContent HTML content to convert to PDF
   * @returns Base64 encoded PDF data with metadata
   */
  static async exportToPdf(htmlContent: string, documentName?: string): Promise<ExportResult> {
    logger.info('Exporting content to PDF');
    const startTime = Date.now();

    captureEvent('client_pdf_export_started', {
      htmlContentLength: htmlContent.length,
    });

    try {
      const result = await httpRequest<ExportResult>(`${this.baseUrl}/pdf`, {
        method: 'POST',
        data: {
          htmlContent,
          ...(documentName ? { documentName } : {})
        }
      });

      logger.info('Content exported to PDF successfully', {
        filename: result.filename,
        size: result.size
      });

      captureEvent('client_pdf_export_completed', {
        filename: result.filename,
        fileSize: result.size,
        processingTimeMs: Date.now() - startTime,
      });

      return result;
    } catch (error) {
      logger.error('Error exporting content to PDF:', error);
      const err = error instanceof Error ? error : new Error('PDF export failed');
      captureException(err, {
        htmlContentLength: htmlContent.length,
        processingTimeMs: Date.now() - startTime,
      });
      sentryCaptureException(err, { exportType: 'pdf' });
      throw err;
    }
  }

  /**
   * Export entities to Markdown format
   * @param entities Array of entities to export
   * @returns Base64 encoded Markdown data with metadata
   */
  static async exportToMd(
    entities: Entity[],
    unresolvedVisualIdentities: UnresolvedVisualIdentity[] = [],
    humanIdentityReviews: HumanIdentityReview[] = []
  ): Promise<ExportResult> {
    logger.info('Exporting entities to Markdown', {
      entityCount: entities.length,
    });
    const startTime = Date.now();

    captureEvent('client_md_export_started', {
      entityCount: entities.length,
    });

    try {
      const result = await httpRequest<ExportResult>(`${this.baseUrl}/md`, {
        method: 'POST',
        data: {
          entities,
          unresolvedVisualIdentities,
          humanIdentityReviews
        }
      });

      logger.info('Entities exported to Markdown successfully', {
        filename: result.filename
      });

      captureEvent('client_md_export_completed', {
        entityCount: entities.length,
        filename: result.filename,
        processingTimeMs: Date.now() - startTime,
      });

      return result;
    } catch (error) {
      logger.error('Error exporting entities to Markdown:', error);
      const err = error instanceof Error ? error : new Error('Markdown export failed');
      captureException(err, {
        entityCount: entities.length,
        processingTimeMs: Date.now() - startTime,
      });
      sentryCaptureException(err, { exportType: 'markdown', entityCount: entities.length });
      throw err;
    }
  }

  /**
   * Convert base64 data to Blob and trigger download
   * @param base64Data Base64 encoded string
   * @param contentType MIME type of the file
   * @param filename Name to save the file as
   */
  static downloadBase64File(base64Data: string, contentType: string, filename: string): void {
    try {
      // Convert base64 to binary
      const byteCharacters = atob(base64Data);
      const byteArrays = [];

      for (let offset = 0; offset < byteCharacters.length; offset += 512) {
        const slice = byteCharacters.slice(offset, offset + 512);
        const byteNumbers = new Array(slice.length);
        
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }
        
        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
      }

      // Create blob and download link
      const blob = new Blob(byteArrays, { type: contentType });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      setTimeout(() => {
        URL.revokeObjectURL(link.href);
        document.body.removeChild(link);
      }, 100);

      logger.info('File download triggered', { filename });
    } catch (error) {
      logger.error('Error downloading file:', error);
      throw error instanceof Error ? error : new Error('Error downloading file');
    }
  }
}
